import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';
import { AddressAutocomplete } from '../components/ui/AddressAutocomplete';
import {
  accessoryProductsApi, accessoryOrdersApi, deviceProfilesApi,
  AccessoryProduct, AccessoryOrderLine, AccessoryOrderAddress, DeviceProfile,
} from '../api/accessoryShop';

const CART_KEY = 'intrlock.accessories.cart';
const ADDR_KEY = 'intrlock.accessories.address';

const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;

const loadCart = (): AccessoryOrderLine[] => {
  try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch { return []; }
};
const loadAddr = (): AccessoryOrderAddress => {
  try {
    const v = JSON.parse(localStorage.getItem(ADDR_KEY) || 'null');
    if (v) return v;
  } catch {}
  return { first_name: '', last_name: '', email: '', phone: '', address1: '', city: '', region: '', zip: '', country: 'US' };
};

// ─── Customer-facing catalog + cart ──────────────────────────────────────────

interface CatalogProps { admin: boolean; onSwitchToAdmin: () => void; }

const Catalog: React.FC<CatalogProps> = ({ admin, onSwitchToAdmin }) => {
  const [products, setProducts] = useState<AccessoryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [selected, setSelected] = useState<AccessoryProduct | null>(null);
  const [selectedQty, setSelectedQty] = useState(1);
  const [cart, setCart] = useState<AccessoryOrderLine[]>(loadCart());
  const [view, setView] = useState<'shop' | 'cart' | 'placed'>('shop');
  const [address, setAddress] = useState<AccessoryOrderAddress>(loadAddr());
  const [orderError, setOrderError] = useState<string | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState<string | null>(null);

  useEffect(() => {
    accessoryProductsApi.listActive()
      .then(setProducts)
      .catch(e => setError(e?.message || 'Failed to load products'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('paid_session');
    const canceled = params.get('canceled');
    if (canceled) {
      setOrderError('Payment was canceled — your cart was kept.');
      const u = new URL(window.location.href);
      u.searchParams.delete('canceled'); u.searchParams.delete('order_id');
      window.history.replaceState(null, '', u.toString());
      return;
    }
    if (sessionId) {
      (async () => {
        try {
          const order = await accessoryOrdersApi.syncSession(sessionId);
          setPlacedOrderId(order.id);
          setCart([]);
          setView('placed');
          const u = new URL(window.location.href);
          u.searchParams.delete('paid_session'); u.searchParams.delete('order_id');
          window.history.replaceState(null, '', u.toString());
        } catch (e: any) {
          setOrderError(`Payment received, but order sync failed: ${e?.message || e}`);
        }
      })();
    }
  }, []);

  const categories = useMemo(() => {
    const s = new Set<string>();
    products.forEach(p => p.category && s.add(p.category));
    return ['All', ...Array.from(s).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    return categoryFilter === 'All' ? products : products.filter(p => p.category === categoryFilter);
  }, [products, categoryFilter]);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const subtotal = cart.reduce((s, i) => s + i.unitPrice * i.quantity, 0);

  const addToCart = () => {
    if (!selected) return;
    setCart(prev => {
      const existing = prev.find(i => i.productId === selected.id);
      if (existing) return prev.map(i => i === existing ? { ...i, quantity: i.quantity + selectedQty } : i);
      return [...prev, {
        productId: selected.id,
        productTitle: selected.name,
        unitPrice: selected.price_cents,
        quantity: selectedQty,
        imageSrc: selected.image_url || undefined,
      }];
    });
    setSelected(null);
    setSelectedQty(1);
  };

  const updateQty = (idx: number, qty: number) => {
    if (qty < 1) return;
    setCart(prev => prev.map((i, n) => n === idx ? { ...i, quantity: qty } : i));
  };
  const removeLine = (idx: number) => setCart(prev => prev.filter((_, n) => n !== idx));

  const canCheckout = cart.length > 0
    && address.first_name && address.last_name && address.email
    && address.address1 && address.city && address.region && address.zip;

  const checkout = async () => {
    setOrderLoading(true);
    setOrderError(null);
    try {
      localStorage.setItem(ADDR_KEY, JSON.stringify(address));
      const { url } = await accessoryOrdersApi.createCheckoutSession({
        cart, address, return_origin: window.location.origin,
      });
      if (!url) throw new Error('Stripe did not return a checkout URL');
      window.location.href = url;
    } catch (e: any) {
      setOrderError(e?.message || 'Checkout failed');
      setOrderLoading(false);
    }
  };

  const Toolbar = (
    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
      <div className="flex gap-2">
        <Button variant={view === 'shop' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('shop')}>Shop</Button>
        <Button variant={view === 'cart' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('cart')}>
          Cart{cartCount > 0 ? ` (${cartCount})` : ''}
        </Button>
      </div>
      {admin && (
        <Button variant="ghost" size="sm" onClick={onSwitchToAdmin}>Manage products →</Button>
      )}
    </div>
  );

  if (view === 'placed') {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <Card>
          <div className="text-center">
            <div className="text-4xl mb-2">✓</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment received</h2>
            <p className="text-gray-600 mb-2">Order reference: <code className="bg-gray-100 px-2 py-1 rounded">{placedOrderId}</code></p>
            <p className="text-gray-600 text-sm mb-6">Your order is queued for review. It will ship once an admin approves and fulfills it.</p>
            <div className="flex justify-center gap-2">
              <Button onClick={() => setView('shop')} variant="secondary">Back to shop</Button>
              <Button onClick={() => { window.location.href = '/accessory-shop-orders'; }}>View orders</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (view === 'cart') {
    return (
      <div className="space-y-4">
        {Toolbar}
        <Card>
          <h2 className="text-lg font-semibold mb-4">Cart</h2>
          {cart.length === 0 ? (
            <div className="text-gray-500 py-6 text-center">Your cart is empty.</div>
          ) : (
            <div className="divide-y">
              {cart.map((item, idx) => (
                <div key={item.productId} className="py-3 flex items-center gap-3">
                  <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {item.imageSrc && <img src={item.imageSrc} alt={item.productTitle} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{item.productTitle}</div>
                    <div className="text-sm text-gray-500">{fmt(item.unitPrice)} each</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(idx, item.quantity - 1)} className="w-7 h-7 border border-gray-300 rounded hover:bg-gray-50">−</button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button onClick={() => updateQty(idx, item.quantity + 1)} className="w-7 h-7 border border-gray-300 rounded hover:bg-gray-50">+</button>
                  </div>
                  <div className="w-20 text-right font-medium">{fmt(item.unitPrice * item.quantity)}</div>
                  <button onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700 px-2">✕</button>
                </div>
              ))}
              <div className="pt-3 flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">{fmt(subtotal)}</span>
              </div>
            </div>
          )}
        </Card>
        {cart.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">Shipping address</h2>
            <form autoComplete="on" onSubmit={e => e.preventDefault()}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input className="border rounded px-3 py-2" placeholder="First name" autoComplete="given-name" name="fname" value={address.first_name} onChange={e => setAddress({ ...address, first_name: e.target.value })} />
                <input className="border rounded px-3 py-2" placeholder="Last name" autoComplete="family-name" name="lname" value={address.last_name} onChange={e => setAddress({ ...address, last_name: e.target.value })} />
                <input className="border rounded px-3 py-2 sm:col-span-2" type="email" placeholder="Email" autoComplete="email" name="email" value={address.email} onChange={e => setAddress({ ...address, email: e.target.value })} />
                <input className="border rounded px-3 py-2 sm:col-span-2" type="tel" placeholder="Phone" autoComplete="tel" name="tel" value={address.phone} onChange={e => setAddress({ ...address, phone: e.target.value })} />
                <div className="sm:col-span-2 -mb-4">
                  <AddressAutocomplete
                    label=""
                    placeholder="Start typing an address…"
                    value={address.address1}
                    onChange={(street) => setAddress(a => ({ ...a, address1: street }))}
                    onSelect={(p) => setAddress(a => ({
                      ...a,
                      address1: p.street || a.address1,
                      city: p.city || a.city,
                      region: p.state || a.region,
                      zip: p.zip || a.zip,
                      country: a.country || 'US',
                    }))}
                  />
                </div>
                <input className="border rounded px-3 py-2" placeholder="City" autoComplete="address-level2" name="city" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} />
                <input className="border rounded px-3 py-2" placeholder="State (e.g. CA)" autoComplete="address-level1" name="state" value={address.region} onChange={e => setAddress({ ...address, region: e.target.value })} />
                <input className="border rounded px-3 py-2" placeholder="ZIP" autoComplete="postal-code" name="zip" value={address.zip} onChange={e => setAddress({ ...address, zip: e.target.value })} />
                <input className="border rounded px-3 py-2" placeholder="Country (US)" autoComplete="country" name="country" value={address.country} onChange={e => setAddress({ ...address, country: e.target.value })} />
              </div>
            </form>
            <div className="mt-4 pt-4 border-t flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Total: <span className="font-semibold text-gray-900">{fmt(subtotal)}</span>
                <span className="text-xs text-gray-400 ml-2">(shipping computed at fulfillment)</span>
              </div>
              <div className="flex items-center gap-3">
                {orderError && <span className="text-sm text-red-600">{orderError}</span>}
                <Button onClick={checkout} loading={orderLoading} disabled={!canCheckout}>
                  {canCheckout ? 'Pay with Stripe' : 'Fill in address to continue'}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Toolbar}
      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setCategoryFilter(c)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                categoryFilter === c
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-400'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {error && <Card><div className="text-red-700 text-sm">{error}</div></Card>}

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} padding={false} className="overflow-hidden">
              <div className="aspect-square bg-gray-100 animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-gray-100 rounded animate-pulse" />
                <div className="h-3 w-2/3 bg-gray-100 rounded animate-pulse" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <span className="text-5xl mb-3 block">🛒</span>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">No products yet</h2>
            <p className="text-sm text-gray-500">
              {admin ? 'Add your first accessory using the "Manage products" button above.' : 'Check back soon.'}
            </p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {!loading && filtered.map(p => (
          <Card key={p.id} padding={false} className="overflow-hidden cursor-pointer">
            <button type="button" onClick={() => { setSelected(p); setSelectedQty(1); }} className="block w-full text-left">
              <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                ) : <div className="text-gray-400 text-sm">No image</div>}
              </div>
              <div className="p-4">
                <div className="font-semibold text-gray-900 line-clamp-2 min-h-[2.75rem]">{p.name}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-emerald-700 font-semibold">{fmt(p.price_cents)}</div>
                  {p.stock === 0 && <Badge color="red">Out of stock</Badge>}
                  {typeof p.stock === 'number' && p.stock > 0 && p.stock <= 5 && <Badge color="yellow">Only {p.stock} left</Badge>}
                </div>
              </div>
            </button>
          </Card>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="bg-gray-50 aspect-square flex items-center justify-center">
                {selected.image_url
                  ? <img src={selected.image_url} alt={selected.name} className="w-full h-full object-contain" />
                  : <div className="text-gray-400">No image</div>}
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h2 className="text-xl font-bold text-gray-900">{selected.name}</h2>
                  <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700">✕</button>
                </div>
                <div className="text-emerald-700 font-semibold mb-3">{fmt(selected.price_cents)}</div>
                {selected.category && <div className="mb-2"><Badge color="gray">{selected.category}</Badge></div>}
                <p className="text-sm text-gray-700 my-4 whitespace-pre-line">{selected.description || 'No description.'}</p>
                {selected.stock === 0 ? (
                  <div className="text-red-600 text-sm mb-4">Out of stock</div>
                ) : (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Quantity</label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedQty(q => Math.max(1, q - 1))} className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-50">−</button>
                      <input
                        type="number" min={1}
                        max={typeof selected.stock === 'number' ? selected.stock : undefined}
                        value={selectedQty}
                        onChange={e => setSelectedQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-16 text-center border border-gray-300 rounded px-2 py-1"
                      />
                      <button onClick={() => setSelectedQty(q => q + 1)} className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-50">+</button>
                    </div>
                  </div>
                )}
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Line total: <span className="font-semibold text-gray-900">{fmt(selected.price_cents * selectedQty)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => setSelected(null)} variant="secondary">Cancel</Button>
                    <Button onClick={addToCart} disabled={selected.stock === 0}>Add to cart</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Admin product management ───────────────────────────────────────────────

interface BlankProduct { name: string; description: string; category: string; price_cents: number; image_url: string; stock: string; active: boolean; device_profile_id: string; }
const blank: BlankProduct = { name: '', description: '', category: '', price_cents: 0, image_url: '', stock: '', active: true, device_profile_id: '' };

const AdminProducts: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [products, setProducts] = useState<AccessoryProduct[]>([]);
  const [profiles, setProfiles] = useState<DeviceProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AccessoryProduct | null>(null);
  const [creating, setCreating] = useState<BlankProduct | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [prods, profs] = await Promise.all([
        accessoryProductsApi.listAll(),
        deviceProfilesApi.list().catch(() => [] as DeviceProfile[]),
      ]);
      setProducts(prods);
      setProfiles(profs);
    } catch (e: any) { setError(e?.message || 'Failed to load products'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const profileLabel = (id?: string | null) => {
    if (!id) return null;
    const p = profiles.find(x => x.id === id);
    return p ? `${p.name} (${p.role}${p.has_cellular ? ', cellular' : ''})` : id.slice(0, 8) + '…';
  };

  const save = async (data: Partial<AccessoryProduct>) => {
    setSaving(true);
    try {
      if (editing) await accessoryProductsApi.update(editing.id, data);
      else await accessoryProductsApi.create(data);
      setEditing(null); setCreating(null);
      await load();
    } catch (e: any) { setError(e?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (p: AccessoryProduct) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await accessoryProductsApi.remove(p.id); await load(); }
    catch (e: any) { setError(e?.message || 'Delete failed'); }
  };

  const toggle = async (p: AccessoryProduct) => {
    try { await accessoryProductsApi.update(p.id, { active: !p.active }); await load(); }
    catch (e: any) { setError(e?.message || 'Update failed'); }
  };

  const form = editing || creating;
  const formData: BlankProduct = editing ? {
    name: editing.name, description: editing.description || '', category: editing.category || '',
    price_cents: editing.price_cents, image_url: editing.image_url || '',
    stock: editing.stock === null || editing.stock === undefined ? '' : String(editing.stock),
    active: editing.active !== false,
    device_profile_id: editing.device_profile_id || '',
  } : creating || blank;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Manage accessory products</h2>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onBack}>← Back to shop</Button>
          <Button size="sm" onClick={() => setCreating(blank)}>+ New product</Button>
        </div>
      </div>

      {error && <Card><div className="text-red-700 text-sm">{error}</div></Card>}

      {loading && <Card><div className="text-gray-500 text-center py-4">Loading…</div></Card>}

      {!loading && products.length === 0 && (
        <Card><div className="text-gray-500 text-center py-6">No products yet — click "+ New product".</div></Card>
      )}

      {!loading && products.length > 0 && (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="p-3">Image</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Category</th>
                  <th className="p-3 text-right">Price</th>
                  <th className="p-3 text-right">Stock</th>
                  <th className="p-3">Device profile</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map(p => (
                  <tr key={p.id}>
                    <td className="p-3">
                      <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden">
                        {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-500 line-clamp-1">{p.description}</div>
                    </td>
                    <td className="p-3 text-gray-600">{p.category || '—'}</td>
                    <td className="p-3 text-right font-medium">{fmt(p.price_cents)}</td>
                    <td className="p-3 text-right">{p.stock === null || p.stock === undefined ? '∞' : p.stock}</td>
                    <td className="p-3">
                      {p.device_profile_id
                        ? <span className="text-xs text-emerald-700">{profileLabel(p.device_profile_id)}</span>
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="p-3">
                      {p.active ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => toggle(p)}>{p.active ? 'Hide' : 'Show'}</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(p)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {form && (
        <ProductForm
          initial={formData}
          isCreate={!editing}
          saving={saving}
          profiles={profiles}
          onCancel={() => { setEditing(null); setCreating(null); }}
          onSubmit={(data) => save({
            name: data.name.trim(),
            description: data.description,
            category: data.category,
            price_cents: data.price_cents,
            image_url: data.image_url,
            stock: data.stock.trim() === '' ? null : Number(data.stock),
            active: data.active,
            device_profile_id: data.device_profile_id || null,
          })}
        />
      )}
    </div>
  );
};

const ProductForm: React.FC<{
  initial: BlankProduct;
  isCreate: boolean;
  saving: boolean;
  profiles: DeviceProfile[];
  onCancel: () => void;
  onSubmit: (data: BlankProduct) => void;
}> = ({ initial, isCreate, saving, profiles, onCancel, onSubmit }) => {
  const [d, setD] = useState<BlankProduct>(initial);
  const [priceDollars, setPriceDollars] = useState<string>((initial.price_cents / 100).toFixed(2));

  const valid = d.name.trim().length > 0 && d.price_cents > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{isCreate ? 'New product' : 'Edit product'}</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input className="w-full border rounded px-3 py-2" value={d.name} onChange={e => setD({ ...d, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea rows={3} className="w-full border rounded px-3 py-2" value={d.description} onChange={e => setD({ ...d, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <input className="w-full border rounded px-3 py-2" placeholder="e.g. Mounting" value={d.category} onChange={e => setD({ ...d, category: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Price (USD) *</label>
                <input
                  type="number" min={0} step="0.01"
                  className="w-full border rounded px-3 py-2"
                  value={priceDollars}
                  onChange={e => {
                    const v = e.target.value;
                    setPriceDollars(v);
                    const n = Math.round(parseFloat(v) * 100);
                    setD({ ...d, price_cents: isNaN(n) ? 0 : n });
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Image URL</label>
                <input className="w-full border rounded px-3 py-2" placeholder="https://…" value={d.image_url} onChange={e => setD({ ...d, image_url: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Stock <span className="text-gray-400">(blank = unlimited)</span>
                </label>
                <input type="number" min={0} className="w-full border rounded px-3 py-2" value={d.stock} onChange={e => setD({ ...d, stock: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Device profile <span className="text-gray-400">(set this for bridge SKUs that should auto-provision on fulfillment)</span>
              </label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={d.device_profile_id}
                onChange={e => setD({ ...d, device_profile_id: e.target.value })}
              >
                <option value="">— Standard accessory (no provisioning) —</option>
                {profiles.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} · role={p.role}{p.has_cellular ? ' · cellular' : ''}
                  </option>
                ))}
              </select>
              {profiles.length === 0 && (
                <div className="text-xs text-amber-700 mt-1">
                  No device profiles defined yet. Visit "Device Profiles" in the Supplies menu to create one.
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={d.active} onChange={e => setD({ ...d, active: e.target.checked })} />
              Active (visible in shop)
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => onSubmit(d)} loading={saving} disabled={!valid}>
              {isCreate ? 'Create product' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Page wrapper ────────────────────────────────────────────────────────────

export const Accessories: React.FC = () => {
  const { user } = useAuth();
  const admin = isSuperAdmin((user?.role || 'customer') as any);
  const [mode, setMode] = useState<'shop' | 'admin'>('shop');

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Accessories</h1>
        <p className="text-gray-500 mt-1">
          {mode === 'admin' ? 'Create and manage accessory products.' : 'Hardware, mounting solutions, and intercom panels.'}
        </p>
      </div>
      <div className="max-w-7xl mx-auto">
        {mode === 'shop'
          ? <Catalog admin={admin} onSwitchToAdmin={() => setMode('admin')} />
          : <AdminProducts onBack={() => setMode('shop')} />}
      </div>
    </div>
  );
};
